/**
 * Validação de CPF e CNS (Cartão Nacional de Saúde).
 *
 * O banco aceita CPF/CNS apenas com dígitos e com o tamanho certo (migration 0011).
 * Aqui conferimos também o DÍGITO VERIFICADOR, para avisar o usuário na hora de
 * digitar ("CPF inválido") em vez de deixar um número errado chegar ao banco.
 */
import { z } from 'zod';

/** Remove tudo que não for dígito: "529.982.247-25" → "52998224725". */
export function somenteDigitos(valor: string): string {
  return valor.replace(/\D/g, '');
}

/** CPF: 11 dígitos, não pode ter todos iguais, e os 2 últimos são calculados pelos 9 primeiros. */
export function cpfValido(valor: string): boolean {
  const cpf = somenteDigitos(valor);
  if (cpf.length !== 11 || /^(\d)\1{10}$/.test(cpf)) return false;

  const digitos = cpf.split('').map(Number);
  const calcularDv = (quantidade: number): number => {
    let soma = 0;
    digitos.slice(0, quantidade).forEach((d, i) => (soma += d * (quantidade + 1 - i)));
    const resto = (soma * 10) % 11;
    return resto === 10 ? 0 : resto;
  };

  return calcularDv(9) === digitos[9] && calcularDv(10) === digitos[10];
}

/**
 * CNS: 15 dígitos.
 * - Começa com 1 ou 2 (definitivo): os 4 últimos dígitos são gerados a partir dos 11 primeiros.
 * - Começa com 7, 8 ou 9 (provisório): a soma ponderada (pesos 15…1) deve ser múltipla de 11.
 */
export function cnsValido(valor: string): boolean {
  const cns = somenteDigitos(valor);
  if (cns.length !== 15) return false;

  const somaPonderada = (texto: string): number =>
    texto.split('').reduce((soma, d, i) => soma + Number(d) * (15 - i), 0);

  const primeiro = cns[0];
  if (primeiro === '1' || primeiro === '2') {
    const base = cns.slice(0, 11);
    let soma = somaPonderada(base);
    let dv = 11 - (soma % 11);
    if (dv === 11) dv = 0;
    let esperado: string;
    if (dv === 10) {
      soma += 2;
      dv = 11 - (soma % 11);
      esperado = `${base}001${dv}`;
    } else {
      esperado = `${base}000${dv}`;
    }
    return cns === esperado;
  }

  if (primeiro === '7' || primeiro === '8' || primeiro === '9') {
    return somaPonderada(cns) % 11 === 0;
  }

  return false;
}

/** "52998224725" → "529.982.247-25" (para exibir). */
export function formatarCpf(valor: string): string {
  const cpf = somenteDigitos(valor);
  if (cpf.length !== 11) return valor;
  return `${cpf.slice(0, 3)}.${cpf.slice(3, 6)}.${cpf.slice(6, 9)}-${cpf.slice(9)}`;
}

/**
 * LGPD — minimização: em listas, mostrar só o meio do CPF.
 * "52998224725" → "***.982.247-**"
 */
export function mascararCpf(valor: string): string {
  const cpf = somenteDigitos(valor);
  if (cpf.length !== 11) return '***';
  return `***.${cpf.slice(3, 6)}.${cpf.slice(6, 9)}-**`;
}

/** "898001160660003" → "898 0011 6066 0003" (para exibir). */
export function formatarCns(valor: string): string {
  const cns = somenteDigitos(valor);
  if (cns.length !== 15) return valor;
  return `${cns.slice(0, 3)} ${cns.slice(3, 7)} ${cns.slice(7, 11)} ${cns.slice(11)}`;
}

/** Schema Zod: aceita com ou sem máscara, valida o DV e entrega só os dígitos. */
export const cpfSchema = z
  .string()
  .trim()
  .transform(somenteDigitos)
  .refine(cpfValido, { message: 'CPF inválido. Confira os números digitados.' });

export const cnsSchema = z
  .string()
  .trim()
  .transform(somenteDigitos)
  .refine(cnsValido, { message: 'Cartão Nacional de Saúde (CNS) inválido. Confira os números.' });
